import { Box, Text } from '@nimbus-ds/components';
import { MetaplanItemResponseDto } from '@tiendanube/common';
import { SubscriptionFrequencyTypeEnum } from '@tiendanube/common/src/domains/subscriptions/enums';
import { PopoverToogle } from 'commons/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';

interface SubscriptionConditionsProps {
  subscriptionOptions: MetaplanItemResponseDto['subscriptionOptions'];
}

export function SubscriptionConditions({
  subscriptionOptions,
}: Readonly<SubscriptionConditionsProps>) {
  const t = useTranslationCatalog();
  const optionsCount = subscriptionOptions?.length || 0;

  const getFrequencyText = (
    frequencyType: SubscriptionFrequencyTypeEnum,
    frequencyParam?: string,
  ): string => {
    if (frequencyType === SubscriptionFrequencyTypeEnum.EVERY_N_DAYS) {
      return t('subscriptions.table.everyNDays', {
        days: frequencyParam,
      });
    }
    return frequencyType;
  };

  const getDiscountText = (discountPercentage: number): string => {
    if (!discountPercentage || discountPercentage === 0) {
      return t('subscriptions.table.noDiscount');
    }
    return t('subscriptions.table.discountOff', {
      percentage: discountPercentage,
    });
  };

  const formatOption = (
    option: NonNullable<typeof subscriptionOptions>[0],
  ): string => {
    const discountText = getDiscountText(option.discountPercentage);
    const frequencyText = getFrequencyText(
      option.frequencyType,
      option.frequencyParam,
    );
    return `${discountText} ${frequencyText}`;
  };

  if (optionsCount === 0) {
    return <Text>-</Text>;
  }

  if (optionsCount === 1) {
    return <Text>{formatOption(subscriptionOptions[0])}</Text>;
  }

  return (
    <PopoverToogle
      label={t('subscriptions.table.conditions', { count: optionsCount })}
      content={
        <Box flexGrow="1">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap="4"
          >
            {subscriptionOptions?.map((option, index) => (
              <Text
                key={`${option.frequencyType}-${option.frequencyParam}-${index}`}
              >
                {formatOption(option)}
              </Text>
            ))}
          </Box>
        </Box>
      }
    />
  );
}
